import React, { useState, useRef, useEffect, SetStateAction } from "react";
import { getLogonInfoFromCookies } from "../utils/util";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PresentationGrid } from "../components/PresentationGrid";
import {
  CmsFilterDescriptorBase,
  CmsFilterDetailRow,
} from "../services/Models/FilterDescriptorModel";
import { SiteParametersResponse } from "../services/Models/SiteParametersModel";
import { AuthResponse } from "../services/Models/AuthModel";
import { localFilter } from "./Home";
import {
  VaporThemeProvider,
  VaporPage,
  Card,
  CardMedia,
  VaporAppBar,
  Spinner,
  VaporUIShellNav,
  NotificationOverflow,
  AlertTitle,
  Box,
} from "@vapor/v3-components";
import { IDmsPageResponse } from "../services/Models/DmsPagination";
import { FilterDetailsResponse } from "../services/Models/FilterDetailsModel";
import { getFilterDetails } from "../services/filterDetailsService";
import { ToolbarButtons } from "../components/ToolbarButtons";
import { PreviewDocument } from "../components/PreviewDocument";
import { ShowFilter } from "../components/ShowFilter";
import { FilterListResponse } from "../services/Models/FilterListModel";
import { getFilterList } from "../services/filterListService";
import { getSiteParameters } from "../services/siteParametersService";
import { useAuth } from "../Router/AuthProvider";

export interface PresentationDoc {
  idObject: number;
  idDoc: number;
  fileName: string;
  fileNameUrl: string;
  documentType: string;
  fileType: string;
  docImage: string;
}
export interface PresentationFilter extends localFilter {
  numeroRighe: number;
  numeroColonne: number;
  stileColonne: string;
  stileFilterName: string;
  documents: PresentationDoc[];
}

const PresentationPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { performLogout } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const loginResult = useRef<AuthResponse>(getLogonInfoFromCookies());
  const localSiteParameters = useRef<SiteParametersResponse>(
    JSON.parse(localStorage.getItem("SiteParameters") ?? "{}")
  );
  const [filters, setFilters] = useState<PresentationFilter[]>([]);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [urlPreview, setUrlPreview] = useState<string>("");
  const [typePreview, setTypePreview] = useState<string | null>("");
  const noDownloadVideo = useRef<boolean>(false);
  const [currentFilter, setCurrentFilter] = useState<localFilter>({
    filterId: 0,
    filterName: "",
    IconFile: "",
    TotPub: 0,
    LinkedDocs: false,
    noDownloadVideo: false,
  });
  const [showIconsOrList, setShowIconOrList] = useState<number>(0);

  useEffect(() => {
    if (loginResult.current)
      loadPresentation(
        loginResult.current,
        localSiteParameters.current.ContPubFiltro
      );
  }, []);

  const loadPresentation = async (
    loginResult: AuthResponse,
    contPubFiltro: boolean,
    reload?: boolean
  ) => {
    setLoading(true);
    try {
      if (reload) {
        const resultSite: SiteParametersResponse = await getSiteParameters(
          null
        );
        if (resultSite && resultSite.ResultCode !== "ERROR") {
          localSiteParameters.current = resultSite;
          if (localSiteParameters.current.Visualizzazione === "") {
            navigate("/home");
          }
          if (localSiteParameters.current.Visualizzazione === "tabs") {
            loadFilters(loginResult);
          }
        } else {
          setError("LoadSiteParameters : " + t("errore interno"));
          console.error("LoadSiteParameters Error :", "no result");
          setLoading(false);
          return;
        }
      }
      const result: IDmsPageResponse<FilterDetailsResponse> =
        await getFilterDetails(
          loginResult.SecurityToken,
          loginResult.Language ?? "it",
          0,
          undefined,
          1,
          0, //data.pageRequest.pageSize,
          // undefined,
          "",
          "",
          "",
          []
        );
      if (result && result.data[0].ResultCode !== "ERROR") {
        console.log("getFiter successful:");
        const presFilters: PresentationFilter[] = result.data[0].FilterList.map(
          (filter: CmsFilterDescriptorBase) => {
            const appFilter: PresentationFilter = {
              filterId: filter.FilterId,
              filterName: filter.FilterName,
              IconFile: filter.IconFile,
              TotPub: filter.TotPub,
              LinkedDocs: filter.LinkedDocs === "1" ? true : false,
              noDownloadVideo:
                filter.DownloadVideo === "nodownload" ? true : false,
              numeroRighe: parseInt(filter.NumeroRighe),
              numeroColonne: parseInt(filter.NumeroColonne),
              stileColonne: filter.stileColonne,
              stileFilterName: filter.StileFilterName,
              documents: filter.Rows.filter(
                (doc: CmsFilterDetailRow) => doc.Doc
              ).map((doc: CmsFilterDetailRow) => {
                const document: PresentationDoc = {
                  idObject: doc.Doc.IdObject,
                  idDoc: doc.Doc.IdDoc,
                  fileName: doc.Doc.FileName,
                  fileNameUrl:
                    doc.Doc.DocumentType === "LINK" ||
                    doc.Doc.DocumentType === "YOUTUBE"
                      ? doc.Doc.LinkUrl
                      : doc.Doc.MD5 + "." + doc.Doc.DocumentType,
                  documentType: doc.Doc.DocumentType,
                  fileType: doc.Doc.FileType,
                  docImage: doc.DocImage,
                };
                return document;
              }),
            };
            return appFilter;
          }
        );
        setFilters(presFilters);
      } else {
        console.error("loadFilters Error :", "no result");
      }
    } catch (error) {
      console.error("loadFilters Error :", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFilters = async (loginResult: AuthResponse) => {
    setLoading(true);
    try {
      const result: FilterListResponse = await getFilterList(
        loginResult.SecurityToken,
        loginResult.Language ?? "it",
        localSiteParameters.current.ContPubFiltro
      );
      if (result && result.ResultCode !== "ERROR") {
        console.log("getFilter successful:");
        const locFilters: localFilter[] = result.Filters.map(
          (filter: CmsFilterDescriptorBase) => {
            const appFilter: localFilter = {
              filterId: filter.FilterId,
              filterName: filter.FilterName,
              IconFile: filter.IconFile,
              TotPub: filter.TotPub,
              LinkedDocs: filter.LinkedDocs === "1" ? true : false,
              noDownloadVideo:
                filter.DownloadVideo === "nodownload" ? true : false,
            };
            return appFilter;
          }
        );

        localStorage.setItem("filterList", JSON.stringify(locFilters));
        if (locFilters.length === 0) setError(t("noEnabledFilters"));
        else navigate(`/filter/${locFilters[0].filterId}`);
      } else {
        setError(t("errore interno"));
        console.error("loadFilters Error :", "no result");
      }
    } catch (error) {
      setError(t("errore interno"));
      console.error("loadFilters Error :", error);
    } finally {
      setLoading(false);
    }
  };

  const onReloadFilters = async () => {
    const loginResult = getLogonInfoFromCookies();
    if (!loginResult)
      //navigate("/logout");
      performLogout();

    if (loginResult && loginResult.SecurityToken && !loading) {
      await loadPresentation(
        loginResult,
        localSiteParameters.current.ContPubFiltro,
        true
      );
    }
  };

  const onClickIconsOrList = () => {
    setShowIconOrList(showIconsOrList === 1 ? 2 : 1);
  };

  const onClickFilter = (idFilter: number) => {
    const appFilter = filters.find((f: localFilter) => f.filterId === idFilter);
    if (appFilter) {
      setShowIconOrList(1);
      setCurrentFilter({
        filterId: appFilter.filterId,
        filterName: appFilter.filterName,
        IconFile: appFilter.IconFile,
        TotPub: appFilter.TotPub,
        LinkedDocs: appFilter.LinkedDocs,
        noDownloadVideo: appFilter.noDownloadVideo,
      });
      setShowFilter(true);
    }
  };

  const onClickDocument = (
    fileType: string,
    fileNameUrl: string,
    filterNoDownloadVideo: boolean
  ) => {
    noDownloadVideo.current = filterNoDownloadVideo;
    setTypePreview(fileType);
    setUrlPreview(fileNameUrl);
    setShowPreview(true);
  };

  const closeFilter = () => {
    setShowFilter(false);
  };

  const presentationPage = () => {
    return (
      <div>
        {showFilter ? (
          <ShowFilter
            currentFilter={currentFilter}
            showIconsOrList={showIconsOrList}
            noTitle={false}
            closeFilter={closeFilter}
          />
        ) : (
          <Box>
            {filters
              .filter(
                (filter: PresentationFilter) => filter.documents.length > 0
              )
              .map((filter: PresentationFilter) => (
                <PresentationGrid
                  presentationFilter={filter}
                  onClickFilter={onClickFilter}
                  onClickDocument={onClickDocument}
                />
              ))}
          </Box>
        )}
        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)", // Sfondo semitrasparente opzionale
            }}
          >
            <Spinner size={64} />
          </Box>
        )}
        {error && (
          <NotificationOverflow severity="error" onClose={() => setError(null)}>
            <AlertTitle>{t("error-header")}</AlertTitle>
            {error}
          </NotificationOverflow>
        )}
      </div>
    );
  };
  return (
    <VaporThemeProvider>
      {localSiteParameters.current.Banner !== "0" ? (
        <VaporUIShellNav
          hideDrawer
          setDrawerOpen={() => {}}
          renderToolbar={
            <VaporAppBar
              disableDrawer
              leftContent={
                <Card>
                  <CardMedia
                    alt="image"
                    component="img"
                    image={window.config.BASE_URL + window.config.LOGO_CMS}
                    style={{
                      maxWidth: "200px",
                      maxHeight: "45px",
                      objectFit: "contain",
                    }}
                  />
                </Card>
              }
              rightContent={
                <ToolbarButtons
                  showReload={true}
                  onReloadFilters={onReloadFilters}
                  showIconsOrList={showIconsOrList}
                  onClickIconsOrList={onClickIconsOrList}
                />
              }
            />
          }
        >
          <>
            {presentationPage()}
            <PreviewDocument
              showPreview={showPreview}
              typePreview={typePreview ?? ""}
              urlPreview={urlPreview}
              noDownloadVideo={noDownloadVideo.current}
              closePreview={() => {
                setShowIconOrList(0);
                setShowPreview(false);
              }}
            />
          </>
        </VaporUIShellNav>
      ) : (
        <>{presentationPage()}</>
      )}
    </VaporThemeProvider>
  );
};
export default PresentationPage;
